import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck, Truck, RotateCcw, XCircle } from "lucide-react";
import {
  ProductGallery,
  VariantPicker,
  QuickViewAddButton,
} from "@/components/Products";
import RecentlyViewed, { recordView } from "@/components/RecentlyViewed";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.16, 1, 0.3, 1];

const fmt = (price) =>
  `$${Number.isInteger(price) ? price : price.toFixed(2)}`;

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { data: products = [], isLoading } = useQuery({
    queryKey: ["products"],
    queryFn: async () => (await axios.get(`${API}/products`)).data,
  });
  const product = useMemo(
    () => products.find((p) => p.id === productId),
    [products, productId]
  );
  const [selectedVariant, setSelectedVariant] = useState(null);

  const related = useMemo(() => {
    if (!product) return [];
    const sameTag = products.filter(
      (p) => p.id !== product.id && p.tag === product.tag
    );
    const others = products.filter(
      (p) => p.id !== product.id && p.tag !== product.tag
    );
    return [...sameTag, ...others].slice(0, 4);
  }, [products, product]);

  useEffect(() => {
    setSelectedVariant(null);
    window.scrollTo(0, 0);
    window.__lenis?.scrollTo(0, { immediate: true });
  }, [productId]);

  useEffect(() => {
    if (product) recordView(product.id);
  }, [product?.id]);

  const activeVariant = selectedVariant || product?.variants?.[0] || null;

  if (isLoading) {
    return (
      <main data-testid="product-page" className="mx-auto max-w-[1600px] px-6 pb-24 pt-32 md:px-12">
        <p className="text-sm uppercase tracking-[0.2em] text-smoke">Loading…</p>
      </main>
    );
  }

  if (!product) {
    return (
      <main data-testid="product-page" className="mx-auto max-w-[1600px] px-6 pb-24 pt-32 md:px-12">
        <p className="font-serif text-3xl font-medium text-ink">
          That good has wandered off.
        </p>
        <button
          data-testid="product-notfound-back"
          onClick={() => navigate("/")}
          className="mt-6 border border-ink px-6 py-3 text-xs uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:border-ember hover:bg-ember"
        >
          Back to the shop
        </button>
      </main>
    );
  }

  return (
    <main
      data-testid="product-page"
      className="mx-auto max-w-[1600px] px-6 pb-24 pt-28 md:px-12 md:pt-32"
    >
      <button
        data-testid="product-back"
        onClick={() => navigate("/#shop")}
        className="mb-10 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-smoke transition-colors duration-300 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
        Back to shop
      </button>

      <div className="grid gap-10 lg:grid-cols-2 lg:gap-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="mx-auto w-full min-w-0 max-w-md lg:mx-0 lg:max-w-none"
        >
          <ProductGallery product={product} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1, ease: EASE }}
          className="flex min-w-0 flex-col"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-smoke">
            {product.tag}
          </p>
          <h1
            data-testid="product-page-title"
            className="mt-3 font-serif text-3xl font-medium tracking-tight text-ink md:text-5xl"
          >
            {product.name}
          </h1>
          <p className="mt-3 text-xs uppercase tracking-[0.15em] text-smoke">
            {product.notes}
          </p>
          <VariantPicker
            product={product}
            selectedId={activeVariant?.id}
            onSelect={setSelectedVariant}
          />
          <div className="mt-8 flex flex-wrap items-center gap-6">
            <span
              data-testid="product-page-price"
              className="font-serif text-4xl font-medium text-ember"
            >
              {fmt(activeVariant ? activeVariant.price : product.price)}
            </span>
            {product.available === false ? (
              <span
                data-testid="sold-out-note"
                className="bg-line px-8 py-4 text-xs uppercase tracking-[0.25em] text-smoke"
              >
                Sold out — back soon
              </span>
            ) : (
              <QuickViewAddButton
                product={product}
                variant={activeVariant}
                onDone={() => {}}
              />
            )}
          </div>
          <p
            data-testid="product-shipping"
            className="mt-6 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-smoke"
          >
            <Truck className="h-4 w-4 text-ember" strokeWidth={1.5} />
            {product.shipping || "Ships worldwide"}
          </p>
          {product.returns && (
            <p
              data-testid="product-returns"
              className="mt-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-smoke"
            >
              {product.returns === "Non-refundable" ? (
                <XCircle className="h-4 w-4 text-ember" strokeWidth={1.5} />
              ) : (
                <RotateCcw className="h-4 w-4 text-ember" strokeWidth={1.5} />
              )}
              {product.returns}
            </p>
          )}
          <p className="mt-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-smoke">
            <ShieldCheck className="h-4 w-4 text-ember" strokeWidth={1.5} />
            Secure checkout · Powered by Shopify
          </p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: EASE }}
            data-testid="product-description"
            className="rich-description mt-8 border-t border-line pt-8 text-sm leading-relaxed text-smoke"
            dangerouslySetInnerHTML={{
              __html:
                product.descriptionHtml ||
                `<p>${product.description || "A clever everyday thing, made to be used."}</p>`,
            }}
          />
        </motion.div>
      </div>

      {related.length > 0 && (
        <section data-testid="related-products" className="mt-24">
          <p className="text-xs uppercase tracking-[0.3em] text-smoke">
            Goes well with
          </p>
          <h2 className="mt-3 font-serif text-3xl font-medium tracking-tight text-ink md:text-4xl">
            More clever <span className="italic text-ember">goods</span>.
          </h2>
          <div className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
            {related.map((p) => (
              <button
                key={p.id}
                data-testid={`related-${p.id}`}
                onClick={() => navigate(`/product/${p.id}`)}
                className="group text-left"
              >
                <div className="aspect-square overflow-hidden border border-line bg-stone">
                  <img
                    src={p.image}
                    alt={p.name}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <p className="mt-3 line-clamp-1 font-serif text-lg font-medium tracking-tight text-ink">
                  {p.name}
                </p>
                <p className="mt-0.5 text-sm text-smoke">{fmt(p.price)}</p>
              </button>
            ))}
          </div>
        </section>
      )}

      <RecentlyViewed currentId={product.id} />
    </main>
  );
}
